import {
  type BrowserDisplaySession,
  type BrowserStreamEvent,
  type BrowserSessionSnapshot,
  type BrowserTabSnapshot,
  type DesktopNativeBrowserResult,
  type DevTunnelRecord,
  type EnvironmentId,
  EventId,
  type ScopedThreadRef,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { useCallback, useEffect, useRef, type MutableRefObject } from "react";
import {
  type BrowserThreadState,
  selectBrowserThreadState,
  useBrowserStateStore,
} from "~/browserStateStore";
import { randomUUID } from "~/lib/utils";
import {
  deriveBrowserNativeSurfaceResultState,
  deriveBrowserNativeSurfaceSessionState,
  formatRemoteWebRtcDisplayError,
  remoteWebRtcFailureReasonForMessage,
  staleRemoteWebRtcConnectingDisplayFailure,
} from "./BrowserPreviewPanel.logic";

const REMOTE_WEBRTC_VISIBLE_CONNECTING_TIMEOUT_MS = 90_000;

type BrowserDisplayUpdatedEvent = Extract<BrowserStreamEvent, { type: "display.updated" }>;

interface UseBrowserDisplayLifecycleOptions {
  readonly activeTab: BrowserTabSnapshot | null;
  readonly activeTabRef: MutableRefObject<BrowserTabSnapshot | null>;
  readonly addressInputFocusedRef: MutableRefObject<boolean>;
  readonly browserDisplay: BrowserDisplaySession | undefined;
  readonly devTunnelRecordsRef: MutableRefObject<ReadonlyArray<DevTunnelRecord>>;
  readonly environmentId: EnvironmentId;
  readonly session: BrowserSessionSnapshot | null;
  readonly sessionRef: MutableRefObject<BrowserSessionSnapshot | null>;
  readonly setAddressValue: (value: string) => void;
  readonly showBrowserError: (error: unknown) => void;
  readonly threadId: ThreadId;
  readonly threadRef: ScopedThreadRef;
}

export function useBrowserDisplayLifecycle(options: UseBrowserDisplayLifecycleOptions): {
  readonly handleBrowserDisplayUpdate: (display: BrowserDisplaySession) => void;
  readonly handleNativeBrowserSurfaceError: (error: unknown) => void;
  readonly handleNativeBrowserSurfaceResult: (result: DesktopNativeBrowserResult) => void;
  readonly handleRemoteWebRtcSurfaceError: (error: unknown) => void;
} {
  const {
    activeTab,
    activeTabRef,
    addressInputFocusedRef,
    browserDisplay,
    devTunnelRecordsRef,
    environmentId,
    session,
    sessionRef,
    setAddressValue,
    showBrowserError,
    threadId,
    threadRef,
  } = options;
  const handleBrowserDisplayUpdate = useCallback(
    (display: BrowserDisplaySession) => {
      const latestSession = sessionRef.current;
      const browserState = useBrowserStateStore.getState();
      const currentThreadState = selectBrowserThreadState(
        browserState.browserStateByThreadKey,
        threadRef,
      );
      const baseSession = resolveBrowserDisplayUpdateSession({
        latestSession,
        currentThreadState,
      });
      if (!baseSession) {
        return;
      }
      if (!currentThreadState?.session) {
        browserState.setSessionSnapshot(threadRef, baseSession);
      }
      browserState.applyBrowserEvent(
        threadRef,
        createBrowserDisplayUpdatedEvent({
          session: baseSession,
          display,
          environmentId,
          threadId,
        }),
      );
    },
    [environmentId, sessionRef, threadId, threadRef],
  );

  const remoteWebRtcVisibleConnectingWatchKey = [
    browserDisplay?.transport === "remote-webrtc" ? browserDisplay.status : "not-remote-webrtc",
    session?.browserId ?? "no-browser",
    session?.sessionId ?? "no-session",
    activeTab?.tabId ?? "no-tab",
    browserDisplay?.reconnect?.attempt ?? 0,
  ].join(":");
  const remoteWebRtcVisibleConnectingWatchRef = useRef({
    activeTabId: activeTab?.tabId,
    browserDisplay,
    browserId: session?.browserId,
    sessionId: session?.sessionId,
  });
  remoteWebRtcVisibleConnectingWatchRef.current = {
    activeTabId: activeTab?.tabId,
    browserDisplay,
    browserId: session?.browserId,
    sessionId: session?.sessionId,
  };

  const handleRemoteWebRtcSurfaceError = useCallback(
    (error: unknown) => {
      if (!browserHostSupportsRemoteWebRtc()) {
        handleBrowserDisplayUpdate({
          transport: "remote-webrtc",
          status: "unsupported",
          reason: "unsupported_host",
          detail:
            "This browser host does not support RTCPeerConnection, so live WebRTC display cannot attach.",
          reconnect: { status: "exhausted", attempt: 0 },
        });
        return;
      }
      const message = formatRemoteWebRtcDisplayError(error);
      handleBrowserDisplayUpdate({
        transport: "remote-webrtc",
        status: "failed",
        reason: remoteWebRtcFailureReasonForMessage(message),
        detail: message,
        reconnect: { status: "exhausted", attempt: 0 },
      });
    },
    [handleBrowserDisplayUpdate],
  );

  useEffect(() => {
    const watched = remoteWebRtcVisibleConnectingWatchRef.current;
    if (
      watched.browserDisplay?.transport !== "remote-webrtc" ||
      watched.browserDisplay.status !== "connecting" ||
      !watched.browserId ||
      !watched.sessionId ||
      !watched.activeTabId
    ) {
      return;
    }
    const timeout = window.setTimeout(() => {
      const failedDisplay = staleRemoteWebRtcConnectingDisplayFailure(watched.browserDisplay);
      if (failedDisplay) {
        handleBrowserDisplayUpdate(failedDisplay);
      }
    }, REMOTE_WEBRTC_VISIBLE_CONNECTING_TIMEOUT_MS);
    return () => window.clearTimeout(timeout);
  }, [handleBrowserDisplayUpdate, remoteWebRtcVisibleConnectingWatchKey]);

  const handleNativeBrowserSurfaceResult = useCallback(
    (result: DesktopNativeBrowserResult) => {
      const latestSession = sessionRef.current;
      if (!latestSession?.browserId || !latestSession.sessionId) {
        return;
      }
      const browserState = useBrowserStateStore.getState();
      const currentThreadState = selectBrowserThreadState(
        browserState.browserStateByThreadKey,
        threadRef,
      );
      const nativeSessionState = deriveBrowserNativeSurfaceSessionState({
        latestSession,
        currentThreadSession: currentThreadState?.session,
      });
      if (!nativeSessionState) {
        return;
      }
      if (nativeSessionState.shouldSeedThreadSession) {
        browserState.setSessionSnapshot(threadRef, nativeSessionState.baseSession);
      }
      const nativeResultState = deriveBrowserNativeSurfaceResultState({
        result,
        baseSession: nativeSessionState.baseSession,
        activeTab: activeTabRef.current,
        addressInputFocused: addressInputFocusedRef.current,
        devTunnelRecords: devTunnelRecordsRef.current,
        environmentId,
        threadId,
        eventId: EventId.make(randomUUID()),
        createdAt: new Date().toISOString(),
      });
      if (nativeResultState.addressValue) {
        setAddressValue(nativeResultState.addressValue);
      }
      handleBrowserDisplayUpdate(result.display);
      if (nativeResultState.tabUpdatedEvent) {
        browserState.applyBrowserEvent(threadRef, nativeResultState.tabUpdatedEvent);
      }
    },
    [
      activeTabRef,
      addressInputFocusedRef,
      devTunnelRecordsRef,
      environmentId,
      handleBrowserDisplayUpdate,
      sessionRef,
      setAddressValue,
      threadId,
      threadRef,
    ],
  );

  return {
    handleBrowserDisplayUpdate,
    handleNativeBrowserSurfaceError: showBrowserError,
    handleNativeBrowserSurfaceResult,
    handleRemoteWebRtcSurfaceError,
  };
}

function resolveBrowserDisplayUpdateSession(input: {
  readonly latestSession: BrowserSessionSnapshot | null;
  readonly currentThreadState: BrowserThreadState | null;
}): BrowserSessionSnapshot | null {
  if (!input.latestSession?.browserId || !input.latestSession.sessionId) {
    return null;
  }

  const baseSession = input.currentThreadState?.session ?? input.latestSession;
  if (!baseSession?.browserId || !baseSession.sessionId) {
    return null;
  }

  return baseSession;
}

function createBrowserDisplayUpdatedEvent(input: {
  readonly session: BrowserSessionSnapshot;
  readonly display: BrowserDisplaySession;
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
}): BrowserDisplayUpdatedEvent {
  return {
    eventId: EventId.make(randomUUID()),
    browserId: input.session.browserId,
    environmentId: input.environmentId,
    threadId: input.threadId,
    userId: input.session.userId,
    agentId: input.session.agentId,
    createdAt: new Date().toISOString(),
    type: "display.updated",
    payload: {
      sessionId: input.session.sessionId,
      display: input.display,
    },
  };
}

function browserHostSupportsRemoteWebRtc(): boolean {
  return typeof globalThis.RTCPeerConnection !== "undefined";
}
